export interface VfyVoiceMarkupSegment {
    start: number;
    end: number;
    rawText: string;
    innerText: string;
}

interface MarkupTagMatch {
    start: number;
    end: number;
}

const openingTagPattern = /<vfyvoice(?=[\s>])/g;
const closingTagPattern = /<\/vfyvoice\s*>/g;

const findTagEnd = (text: string, contentStart: number): number => {
    let quote: "\"" | "'" | null = null;

    for (let index = contentStart; index < text.length; index += 1) {
        const character = text[index];
        if (quote) {
            if (character === quote) {
                quote = null;
            }
            continue;
        }

        if (character === "\"" || character === "'") {
            quote = character;
        } else if (character === ">") {
            return index;
        }
    }

    return -1;
};

const findOpeningTag = (text: string, fromIndex: number): MarkupTagMatch | null => {
    const regex = new RegExp(openingTagPattern.source, "g");
    regex.lastIndex = fromIndex;
    const match = regex.exec(text);
    if (!match) {
        return null;
    }

    const tagEnd = findTagEnd(text, match.index + match[0].length);
    return {
        start: match.index,
        end: tagEnd >= 0 ? tagEnd + 1 : -1
    };
};

const findClosingTag = (text: string, fromIndex: number): MarkupTagMatch | null => {
    const regex = new RegExp(closingTagPattern.source, "g");
    regex.lastIndex = fromIndex;
    const match = regex.exec(text);
    if (!match) {
        return null;
    }

    return {
        start: match.index,
        end: match.index + match[0].length
    };
};

const isSelfClosingTag = (text: string, tag: MarkupTagMatch): boolean => (
    /\/\s*>$/.test(text.substring(tag.start, tag.end))
);

const findMatchingClosingTag = (text: string, openingTag: MarkupTagMatch): MarkupTagMatch | null => {
    let depth = 1;
    let cursor = openingTag.end;

    while (cursor < text.length) {
        const nextOpeningTag = findOpeningTag(text, cursor);
        const nextClosingTag = findClosingTag(text, cursor);
        if (!nextClosingTag) {
            return null;
        }

        if (nextOpeningTag && nextOpeningTag.start < nextClosingTag.start) {
            if (nextOpeningTag.end < 0) {
                return null;
            }
            if (!isSelfClosingTag(text, nextOpeningTag)) {
                depth += 1;
            }
            cursor = nextOpeningTag.end;
            continue;
        }

        depth -= 1;
        if (depth === 0) {
            return nextClosingTag;
        }
        cursor = nextClosingTag.end;
    }

    return null;
};

/**
 * Finds conservative, paired vfyvoice ranges while retaining their source text
 * byte-for-byte. Balanced nested pairs are treated as one outer segment;
 * malformed, self-closing, or unmatched tags remain visible to callers.
 */
export const findVfyVoiceMarkupSegments = (text: string): VfyVoiceMarkupSegment[] => {
    const segments: VfyVoiceMarkupSegment[] = [];
    let cursor = 0;

    while (cursor < text.length) {
        const openingTag = findOpeningTag(text, cursor);
        if (!openingTag) {
            break;
        }

        if (openingTag.end < 0) {
            cursor = openingTag.start + 1;
            continue;
        }

        if (isSelfClosingTag(text, openingTag)) {
            cursor = openingTag.end;
            continue;
        }

        const closingTag = findMatchingClosingTag(text, openingTag);
        if (!closingTag) {
            cursor = openingTag.start + 1;
            continue;
        }

        segments.push({
            start: openingTag.start,
            end: closingTag.end,
            rawText: text.substring(openingTag.start, closingTag.end),
            innerText: text.substring(openingTag.end, closingTag.start)
        });
        cursor = closingTag.end;
    }

    return segments;
};

const replaceOpeningTagVoiceName = (openingTag: string, voiceName: string): string => {
    const nameAttributePattern = /(\sname\s*=\s*)(["'])[^"']*\2/i;
    if (nameAttributePattern.test(openingTag)) {
        return openingTag.replace(nameAttributePattern, `$1$2${voiceName}$2`);
    }

    return openingTag.replace(/>$/, ` name="${voiceName}">`);
};

const updateOpeningTagsInSegment = (text: string, voiceName: string): string => {
    const openingTags: MarkupTagMatch[] = [];
    let cursor = 0;

    while (cursor < text.length) {
        const openingTag = findOpeningTag(text, cursor);
        if (!openingTag || openingTag.end < 0) {
            break;
        }
        openingTags.push(openingTag);
        cursor = openingTag.end;
    }

    return openingTags.reduceRight((result, openingTag) => (
        `${result.substring(0, openingTag.start)}`
        + `${replaceOpeningTagVoiceName(result.substring(openingTag.start, openingTag.end), voiceName)}`
        + `${result.substring(openingTag.end)}`
    ), text);
};

/**
 * Updates every balanced vfyvoice opening tag while preserving the wrapped text,
 * all other attributes, and malformed markup byte-for-byte.
 */
export const updateVfyVoiceName = (text: string, voiceName: string): string => {
    if (!text || !voiceName.trim()) {
        return text;
    }

    return findVfyVoiceMarkupSegments(text).reduceRight((result, segment) => {
        const updatedSegment = updateOpeningTagsInSegment(segment.rawText, voiceName.trim().toLowerCase());
        return `${result.substring(0, segment.start)}${updatedSegment}${result.substring(segment.end)}`;
    }, text);
};
